import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Cpu, Sparkles, MessageSquare, Swords } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import UserCenter from './UserCenter';
import LanguageSwitcher from './LanguageSwitcher';
import Logo from './Logo';

interface TopBarProps {
    className?: string;
    showLogo?: boolean;
    showNav?: boolean;
    /** 'chat' = compact workspace toolbar; 'default' = full navigation */
    variant?: 'default' | 'chat';
}

interface ProductItem {
    icon: typeof Cpu;
    labelKey: string;
    descKey: string;
    href: string;
    color: string;
    bg: string;
    current: boolean;
}

const productItemDefs: ProductItem[] = [
    {
        icon: Sparkles,
        labelKey: 'topbar.product_home',
        descKey: 'topbar.product_home_desc',
        href: import.meta.env.VITE_HOME_URL || 'https://etherworks.cn',
        color: 'text-amber-400',
        bg: 'bg-amber-500/10',
        current: false,
    },
    {
        icon: Cpu,
        labelKey: 'topbar.product_cloud',
        descKey: 'topbar.product_cloud_desc',
        href: import.meta.env.VITE_CLOUD_URL || 'https://cloud.etherworks.cn',
        color: 'text-emerald-400',
        bg: 'bg-emerald-500/10',
        current: false,
    },
    {
        icon: Sparkles,
        labelKey: 'topbar.product_finetune',
        descKey: 'topbar.product_finetune_desc',
        href: import.meta.env.VITE_FINETUNE_URL || 'https://finetune.etherworks.cn',
        color: 'text-blue-400',
        bg: 'bg-blue-500/10',
        current: false,
    },
    {
        icon: MessageSquare,
        labelKey: 'topbar.product_llm',
        descKey: 'topbar.product_llm_desc',
        href: import.meta.env.VITE_LLM_URL || 'https://llm.etherworks.cn',
        color: 'text-indigo-400',
        bg: 'bg-indigo-500/10',
        current: true,
    },
];

export default function TopBar({ className = '', showLogo = true, showNav = true, variant = 'default' }: TopBarProps) {
    const { user } = useAuth();
    const { t } = useTranslation('common');
    const location = useLocation();
    const isAuthPage = location.pathname === '/auth';

    const [productMenuOpen, setProductMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        function handleClick(e: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setProductMenuOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const openMenu = () => {
        clearTimeout(timeoutRef.current);
        setProductMenuOpen(true);
    };
    const closeMenu = () => {
        timeoutRef.current = setTimeout(() => setProductMenuOpen(false), 150);
    };

    if (isAuthPage) return null;

    if (variant === 'chat') {
        return (
            <header className={`h-14 toolbar sticky top-0 z-40 w-full ${className}`}>
                <div className="h-full px-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Link to="/" className="flex items-center gap-2 text-foreground hover:text-primary transition-colors">
                            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary text-primary-foreground">
                                <Logo size="w-5 h-5" />
                            </div>
                            <span className="text-sm font-semibold tracking-tight hidden sm:inline">
                                {import.meta.env.VITE_PRODUCT_NAME || 'Etherworks LLM'}
                            </span>
                        </Link>
                    </div>

                    {/* Right — Actions */}
                    <div className="flex items-center gap-1">
                        <LanguageSwitcher variant="icon" />
                    </div>
                </div>
            </header>
        );
    }

    return (
        <header className={`h-14 toolbar sticky top-0 z-40 w-full ${className}`}>
            <div className="h-full px-4 sm:px-6 flex items-center justify-between">
                <div className="flex items-center">
                    {showLogo && (
                        <Link to="/" className="group flex items-center gap-2.5 transition-all">
                            <div className="relative flex items-center justify-center w-8 h-8 rounded-lg bg-primary text-primary-foreground transition-colors group-hover:bg-primary/90">
                                <Logo size="w-5 h-5" />
                            </div>
                            <span className="text-base font-semibold text-foreground tracking-normal group-hover:text-primary transition-colors">
                                {import.meta.env.VITE_PRODUCT_NAME || 'Etherworks LLM'}
                            </span>
                        </Link>
                    )}
                </div>

                <div className="flex items-center gap-4">
                    {showNav && (
                        <nav className="hidden md:flex items-center gap-1">
                            <div
                                ref={menuRef}
                                className="relative"
                                onMouseEnter={openMenu}
                                onMouseLeave={closeMenu}
                            >
                                <button
                                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-1 ${productMenuOpen
                                        ? 'text-primary bg-primary/10'
                                        : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                                        }`}
                                >
                                    {t('topbar.products')}
                                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${productMenuOpen ? 'rotate-180' : ''}`} />
                                </button>

                                {productMenuOpen && (
                                    <div className="absolute left-0 top-full pt-1.5 z-50">
                                        <div className="w-72 macos-menu p-1 animate-in fade-in zoom-in-95 duration-100">
                                            {productItemDefs.map((item, idx) => {
                                                const Icon = item.icon;
                                                const itemClassName = `flex items-center gap-3 px-2.5 py-2 rounded-md transition-colors ${item.current ? 'bg-primary/10 cursor-default' : 'hover:bg-primary/10'}`;
                                                const content = (
                                                    <>
                                                        <div className={`h-7 w-7 rounded-md ${item.bg} flex items-center justify-center flex-shrink-0`}>
                                                            <Icon className={`w-3.5 h-3.5 ${item.color}`} />
                                                        </div>
                                                        <div>
                                                            <p className="text-[13px] font-medium text-foreground">{t(item.labelKey)}</p>
                                                            <p className="text-[11px] text-muted-foreground">{t(item.descKey)}</p>
                                                        </div>
                                                    </>
                                                );
                                                return (
                                                    <div key={item.href} className={idx > 0 ? 'mt-1 pt-1 border-t border-border/40' : ''}>
                                                        {item.current ? (
                                                            <span className={itemClassName} aria-current="page">{content}</span>
                                                        ) : (
                                                            <a href={item.href} target="_blank" rel="noopener noreferrer" onClick={() => setProductMenuOpen(false)} className={itemClassName}>{content}</a>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <Link
                                to="/arena"
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-1.5 ${location.pathname.startsWith('/arena')
                                    ? 'text-primary bg-primary/10'
                                    : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                                    }`}
                            >
                                <Swords className="h-3.5 w-3.5" />
                                {t('topbar.arena')}
                            </Link>
                            <Link
                                to="/plans"
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${location.pathname === '/plans'
                                    ? 'text-primary bg-primary/10'
                                    : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                                    }`}
                            >
                                {t('topbar.pricing')}
                            </Link>
                            <Link
                                to="/docs/overview/quickstart"
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${location.pathname.startsWith('/docs')
                                    ? 'text-primary bg-primary/10'
                                    : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                                    }`}
                            >
                                {t('topbar.docs')}
                            </Link>
                        </nav>
                    )}

                    <LanguageSwitcher />
                    {user ? (
                        <UserCenter />
                    ) : (
                        <Link
                            to="/auth?mode=login"
                            className="text-sm font-medium bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-lg transition-all shadow-sm hover:shadow-md"
                        >
                            {t('topbar.get_started')}
                        </Link>
                    )}
                </div>
            </div>
        </header>
    );
}
